import { Badge, Tooltip } from '@mantine/core';

/**
 * The sentence that qualifies a capacity verdict — written once (#341, #343, #351).
 *
 * `verdict` bands the minutes that WERE measured and never asks how long ago
 * they arrived, so a network whose sampler died days ago still reads a
 * confident green *Healthy*. Every surface that prints a verdict therefore has
 * to print this caveat beside it, and #343 was exactly the case of one that
 * did not.
 *
 * It lives here rather than in each caller because the wording is the point:
 * an operator who reads "Stale" on the dashboard and clicks through to the
 * ranking must meet the SAME sentence, not a second attempt at saying the same
 * thing. Two copies is how that drifts.
 */
export const staleTooltipLabel = (newestSampleAt: string | null): string =>
  newestSampleAt
    ? `No samples since ${new Date(newestSampleAt).toLocaleString()} — the verdict covers only the minutes measured before then.`
    : 'No samples in this window — nothing has been measured yet.';

export interface CapacityStaleBadgeProps {
  /** `newest_sample_at` off the row or response; null when nothing was ever sampled. */
  newestSampleAt: string | null;
  /**
   * Mantine `Badge` size. Defaults to `sm` — the same default `CapacityBadge`
   * uses — so the caveat can never outgrow the verdict it qualifies.
   */
  size?: string;
}

/**
 * Shape, not size, is what separates this from the verdict.
 *
 * `radius="sm"` against the verdict pill's default `xl` is deliberate: two
 * identically-shaped pills side by side read as two verdicts of equal standing,
 * whereas a squared chip reads as an annotation on the pill beside it. Size is
 * the opposite case — a larger caveat next to a smaller verdict inverts the
 * hierarchy, so the two track each other by sharing a default.
 *
 * Presentation only, and deliberately not gated on staleness: whether a row is
 * stale is the caller's `row.stale` to test, since only the caller knows
 * whether it has room to show the caveat at all.
 */
export const CapacityStaleBadge = ({ newestSampleAt, size = 'sm' }: CapacityStaleBadgeProps) => (
  <Tooltip label={staleTooltipLabel(newestSampleAt)} multiline w={280} withArrow>
    <Badge color="orange" variant="light" radius="sm" size={size}>
      Stale
    </Badge>
  </Tooltip>
);
